import { Trans, useLingui } from "@lingui/react/macro";
import { Settings } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  type BlockerFunction,
  Link,
  matchPath,
  Outlet,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { AppModel } from "../../app/app-model";
import { ErrorPage } from "../../app/error-page";
import { PageHeaderActions } from "../../app/layout/layout-context";
import { Button } from "../../components/ui-kit/base/button";
import { useDialog } from "../../components/ui-kit/dialog-provider";
import { Spinner } from "../../components/ui-kit/spinner";
import { cn } from "../../utils/shadcn-ui";
import { assert } from "../../utils/utils";
import { confirmDiscardEvents } from "./events/events-draft";
import { patientProfileHeaderVisible } from "./profile-header-routes";
import { PatientProfileHeader } from "./profile/patient-profile-header";

export const PatientRoute = observer(function PatientRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const { confirm } = useDialog();
  const { id } = useParams<{ id: string }>();
  assert(id !== undefined, "Patient ID param not found");

  const patientId = parseInt(id);
  const location = useLocation();
  const isProfilePage =
    location.pathname === `/patient/${patientId}` ||
    location.pathname === `/patient/${patientId}/`;
  const showHeader = patientProfileHeaderVisible(location.pathname);
  const patient = useMemo(
    () => props.app.initPatient(patientId),
    [patientId, props.app],
  );

  useEffect(() => {
    const init = async () => {
      await patient.init();
    };

    void init();
  }, [patient]);

  // Unsaved event rows live on the patient, so they outlive its own tabs but not
  // a move to another patient — which makes leaving the last chance to ask.
  const navigate = useNavigate();
  const eventsPath = `/patient/${patientId}/events`;
  const leavingPatient = useCallback<BlockerFunction>(
    ({ nextLocation }) =>
      patient.eventsDraft.startedRowCount() > 0 &&
      matchPath({ path: "/patient/:id", end: false }, nextLocation.pathname)
        ?.params.id !== id,
    [patient, id],
  );
  const blocker = useBlocker(leavingPatient);
  const asking = useRef(false);

  useEffect(() => {
    // One prompt at a time — a second navigation attempt while the
    // confirm is up re-blocks, but the pending await still holds the first
    // target, so the second destination is dropped. Fine for a modal confirm.
    if (blocker.state !== "blocked" || asking.current) {
      return;
    }
    asking.current = true;
    void (async () => {
      const discard = await confirmDiscardEvents(
        confirm,
        patient.eventsDraft.startedRowCount(),
      );
      if (discard) {
        // Makes the prompt honest: proceeding really does drop the rows.
        patient.eventsDraft.reset();
        blocker.proceed?.();
      } else {
        // Keep editing means put the rows back in front of the user, which may
        // mean returning to the tab they were typed on.
        blocker.reset?.();
        patient.eventsDraft.dialogOpen = true;
        if (location.pathname !== eventsPath) {
          void navigate(eventsPath);
        }
      }
      asking.current = false;
    })();
  }, [blocker, confirm, patient, location.pathname, eventsPath, navigate]);

  // A reload or a tab close can't be intercepted, only warned about. Registered
  // once and counted inside the handler, so the draft never becomes a
  // render-time dependency of the whole patient page.
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (patient.eventsDraft.startedRowCount() > 0) e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [patient]);

  if (!patient.loaded || patient.error) {
    return (
      <div className="flex h-full items-center justify-center flex-auto">
        {patient.error ? (
          <ErrorPage
            title={
              patient.error.status === 404
                ? t`Patient not found`
                : t`Error loading patient`
            }
            message={
              patient.error.status === 404
                ? t`Patient with ID ${patientId} does not exist.`
                : patient.error.message
            }
          />
        ) : (
          <Spinner />
        )}
      </div>
    );
  }

  return (
    <>
      {/* Sits in the shared page-title row (next to "Patient #… / <tab>")
          instead of taking its own row above the header. Shown on every tab
          that renders the header; the origin path is passed so "Back to
          profile" returns to the tab it was opened from. */}
      {showHeader && (
        <PageHeaderActions>
          <Button asChild variant="secondary" size="sm">
            <Link
              to={`/patient/${patientId}/settings`}
              state={{ settingsFrom: location.pathname }}
            >
              <Settings className="size-4" />
              <Trans>Patient settings</Trans>
            </Link>
          </Button>
        </PageHeaderActions>
      )}
      {showHeader && (
        // The legacy layout keeps the page-title row above the header, so the
        // header pulls up against it as it did before the redesign.
        <div className={cn(props.app.legacyLayout && "-mt-4")}>
          <PatientProfileHeader
            patient={patient}
            api={props.app.seerlinqApi}
            // On the profile page the collapse toggle moves into the chart tabs'
            // "measurements" row; elsewhere the header keeps its own toggle.
            showCollapseToggle={!isProfilePage}
          />
        </div>
      )}
      <Outlet />
    </>
  );
});
